<template>
  <div
    :class="`modal fade px-0 ${$props.value ? 'show d-block' : 'd-none'}`"
    tabindex="-1"
    role="dialog"
  >
    <div
      class="modal-dialog"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header d-flex align-items-center">
          <h4 class="modal-title text-left">
            Location Hierarchy
          </h4>
          <button
            type="button"
            class="close"
            @click="close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body container-fluid">
          <button
            type="button"
            class="btn btn-link btn-sm p-0 mb-2"
            :disabled="loading"
            @click="fetchLocation"
          >
            <template v-if="!loading">
              Refresh
            </template>
            <template v-else>
              Loading ...
            </template>
          </button>
          <p class="mb-2 font-weight-bold">
            Parent Locations
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm text-primary ml-2"
              role="status"
            >
              <span class="sr-only">Saving...</span>
            </span>
          </p>
          <ul
            v-if="parents.length > 0"
            class="list-group mb-2"
          >
            <li
              v-for="l in parents"
              class="list-group-item d-flex justify-content-between"
              :key="l.id"
            >
              <template v-if="typeof l === 'string'">
                {{ l }}
              </template>
              <template v-else>
                <div>
                  {{ l.labels[ 'es' ] }} <small class="text-muted">{{ locationTypes.find(x => x.key === l.type).name }}</small>
                </div>
                <router-link
                  :to="`/locations/${l.id}`"
                  class="btn btn-link p-0"
                  target="_blank"
                >
                  View
                </router-link>
              </template>
            </li>
          </ul>
          <p class="mb-2 font-weight-bold">
            Child Locations
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm text-primary ml-2"
              role="status"
            >
              <span class="sr-only">Saving...</span>
            </span>
          </p>
          <div
            v-for="(locations, locationType) in children"
            :key="locationType"
            class="mb-2"
          >
            <p class="mb-2 font-weight-bold">
              {{ locations.length }}
              <template v-if="locations.length > 1">
                {{ locationTypes.find(x => x.key === locationType ).namePlural }}
              </template>
              <template v-else>
                {{ locationTypes.find(x => x.key === locationType ).name }}
              </template>
            </p>
            <ul
              class="list-group mb-2"
            >
              <li
                class="list-group-item d-flex justify-content-between"
                v-for="l in locations"
                :key="l.id"
              >
                <template v-if="typeof l === 'string'">
                  {{ l }}
                </template>
                <template v-else>
                  <div>
                    {{ l.labels['es'] }} <small class="text-muted">{{ locationTypes.find(x => x.key === l.type).name }}</small>
                  </div>
                  <router-link
                    :to="`/locations/${l.id}`"
                    class="btn btn-link p-0"
                    target="_blank"
                  >
                    View
                  </router-link>
                </template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import notify from '../../notifications';
import locationTypes from '../../models/location-types';
import http from '../../http';

export default {
    data() {
        return {
            children: {},
            parents: [],
            loading: false,
            msg: '',
            loaded: false
        };
    },
    computed: {
        locationTypes() {
            return locationTypes;
        }
    },
    methods: {
        close() {
            this.loaded = false;
            this.$emit( 'input', false );
        },
        async fetchLocation() {
            this.loaded = true;
            this.loading = true;

            this.msg = '';

            let response = await http.get( `/locations/${this.$props.locationId}/hierarchy` );

            if ( response.status !== 200 ) {
                notify( {
                    type: 'error',
                    title: 'Failed to load location hierarchy'
                } );
                return;
            }

            const relationships = await response.json();

            const parentRequests = [];
            const childRequests = [];

            relationships.filter( relation => relation.subject === this.$props.locationId ).forEach( relation => {
                parentRequests.push( new Promise( async ( resolve ) => {
                    const response = await http.get( `locations/${relation.object}?includeShape=false` );
                    resolve( {
                        relation,
                        response
                    } );
                } ) );
            } );

            relationships.filter( relation => relation.object === this.$props.locationId ).forEach( relation => {
                childRequests.push( new Promise( async ( resolve ) => {
                    const response = await http.get( `locations/${relation.subject}?includeShape=false` );
                    resolve( {
                        relation,
                        response
                    } );
                } ) );
            } );

            const parentResponses = await Promise.all( parentRequests );

            for ( let i = 0; i < parentResponses.length; i++ ) {
                if ( parentResponses[ i ].response.status !== 200 ) {
                    this.parents = [
                        ...this.parents,
                        `Request to ${parentResponses[ i ].url} failed with status ${parentResponses[ i ].status}`
                    ];
                    continue;
                }
                this.parents = [
                    ...this.parents,
                    await parentResponses[ i ].response.json()
                ];
            }

            const childResponses = await Promise.all( childRequests );

            for ( let i = 0; i < childResponses.length; i++ ) {
                const location = await childResponses[ i ].response.json();

                if ( !this.children[ location.type ] ) {
                    this.children[ location.type ] = [];
                }

                if ( childResponses[ i ].response.status !== 200 ) {
                    this.children[ location.type ] = [
                        ...this.children[ location.type ],
                        `Request to ${childResponses[ i ].url} failed with status ${childResponses[ i ].status}`
                    ];
                    continue;
                }

                this.children[ location.type ] = [
                    ...this.children[ location.type ],
                    location
                ];
            }

            this.loading = false;
        }
    },
    async updated() {
        if ( !this.loaded && this.$props.locationId && this.$props.value && !this.loading ) {
            await this.fetchLocation();
        }
    },
    props: {
        value: {
            type: Boolean,
            required: true,
            default: () => false
        },
        locationId: {
            type: Number,
            required: true,
            default: () => 0
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/locations/LocationHierarchyModal.vue